import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, Clock, MapPin } from 'lucide-react';
import { outageService } from '../../../services/outageService';

const TrendingOutages = () => {
  const [trendingOutages, setTrendingOutages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadTrendingOutages();
  }, []);

  const loadTrendingOutages = async () => {
    setLoading(true);
    const { data, error } = await outageService?.getTrendingOutages(6);
    
    if (error) {
      setError(error?.message);
    } else {
      setTrendingOutages(data || []);
    }
    setLoading(false);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'text-red-600 bg-red-100';
      case 'high':
        return 'text-orange-600 bg-orange-100';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100';
      case 'low':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-2 mb-6">
          <TrendingUp className="w-5 h-5 text-orange-600" />
          <h2 className="text-xl font-semibold text-gray-900">Trending Outages</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)]?.map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="p-4 border border-gray-200 rounded-lg">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2 mb-3"></div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-full"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-2 mb-6">
          <TrendingUp className="w-5 h-5 text-orange-600" />
          <h2 className="text-xl font-semibold text-gray-900">Trending Outages</h2>
        </div>
        <div className="text-center text-red-600">
          <p>Failed to load trending outages: {error}</p>
          <button 
            onClick={loadTrendingOutages}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center space-x-2 mb-6">
        <TrendingUp className="w-5 h-5 text-orange-600" />
        <h2 className="text-xl font-semibold text-gray-900">Trending Outages</h2>
      </div>
      {trendingOutages?.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          <TrendingUp className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p>No trending outages at the moment</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {trendingOutages?.map((outage) => (
            <div
              key={outage?.id}
              className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-medium text-gray-900 text-sm line-clamp-2 flex-1">
                  {outage?.title}
                </h3>
                <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(outage?.severity)}`}>
                  {outage?.severity?.toUpperCase()}
                </span>
              </div>

              <div className="text-xs text-gray-500 mb-2">
                <span className="font-medium text-gray-700">
                  {outage?.provider?.name || 'Unknown Provider'}
                </span>
                {outage?.provider?.category && (
                  <span className="ml-1">
                    • {outage?.provider?.category?.replace('_', ' ')}
                  </span>
                )}
              </div>

              {outage?.location_city && (
                <div className="flex items-center space-x-1 text-xs text-gray-500 mb-2">
                  <MapPin className="w-3 h-3" />
                  <span>{outage?.location_city}, {outage?.location_country}</span>
                </div>
              )}

              <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                {outage?.description}
              </p>

              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-1 text-orange-600">
                    <Users className="w-3 h-3" />
                    <span className="font-medium">{outage?.report_count || 0}</span>
                  </div>
                  
                  <div className="flex items-center space-x-1 text-green-600">
                    <TrendingUp className="w-3 h-3" />
                    <span>{outage?.upvote_count || 0}</span>
                  </div>
                </div>

                <div className="flex items-center space-x-1 text-gray-500">
                  <Clock className="w-3 h-3" />
                  <span>{formatTimeAgo(outage?.created_at)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TrendingOutages;